import { useState, useEffect } from 'react';

interface ParticipationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ParticipationModal({ open, onClose }: ParticipationModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setSubmitted(false);
      setError('');
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) data.append(input.name, input.value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d72k08kjuufb4jdbl5jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Ocorreu um erro. Tente novamente.');
      }
    } catch {
      setError('Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-obsidian/92 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-obsidian border border-gold/30 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-cream/50 hover:text-gold transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="mb-8">
            <span className="font-sans text-xs tracking-widest uppercase text-gold block mb-3" style={{ letterSpacing: '0.25em' }}>
              Inscrição
            </span>
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-cream leading-tight">
              Garanta Sua Presença
            </h2>
            <div className="w-10 h-px bg-gold mt-4" />
          </div>

          {submitted ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center border border-gold mx-auto mb-5">
                <i className="ri-check-line text-gold text-2xl" />
              </div>
              <h3 className="font-serif text-xl font-bold text-cream mb-3">Rugido recebido!</h3>
              <p className="font-sans text-sm text-cream/60 font-light leading-relaxed">
                Sua inscrição foi registrada com sucesso. Entraremos em contato em breve com os próximos passos.
              </p>
              <button
                onClick={onClose}
                className="mt-8 font-sans text-xs tracking-widest uppercase border border-gold/60 text-gold px-8 py-3 hover:bg-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ letterSpacing: '0.2em' }}
              >
                Fechar
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="participation-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="nome"
                  required
                  placeholder="Seu nome completo"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  E-mail *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="seu@email.com"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  WhatsApp / Telefone
                </label>
                <input
                  type="tel"
                  name="telefone"
                  placeholder="+1 ou +55"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Cargo / Posição
                </label>
                <input
                  type="text"
                  name="cargo"
                  placeholder="CEO, Diretora, Fundadora..."
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Empresa
                </label>
                <input
                  type="text"
                  name="empresa"
                  placeholder="Nome da sua empresa"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  País de Participação
                </label>
                <select
                  name="pais"
                  className="w-full bg-obsidian border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors font-light cursor-pointer"
                >
                  <option value="">Selecione</option>
                  <option value="EUA">EUA</option>
                  <option value="Brasil">Brasil</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>

              {error && (
                <p className="font-sans text-xs text-wine-light">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full font-sans text-xs tracking-widest uppercase bg-gold text-obsidian py-4 hover:bg-gold-dark transition-all duration-300 cursor-pointer whitespace-nowrap font-semibold disabled:opacity-60 mt-2"
                style={{ letterSpacing: '0.2em' }}
              >
                {loading ? 'Enviando...' : 'Garantir Minha Presença'}
              </button>

              <p className="font-sans text-xs text-cream/30 text-center font-light">
                Suas informações são confidenciais e tratadas com discrição.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
